import { useEffect, useState } from 'react';
import { refreshMe, useMe } from '../lib/api';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'wouter';
import type { Lang } from '@polyglot/engine';
import { isLang } from '@polyglot/engine';
import { Spinner } from '../components/ui';
import { startLocalSession } from '../game/localSession';
import { APP_NAME } from '../brand';
import { UI_LANGS, type UiLang, updateSettings, useSettings } from '../lib/settings';
import { setUiLanguage } from '../i18n';
import { LANG_ENDONYMS } from '@polyglot/engine';

export function Home() {
  const { t } = useTranslation();
  const [, navigate] = useLocation();
  const settings = useSettings();
  const me = useMe();
  useEffect(() => {
    void refreshMe();
  }, []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const quickLang: Lang =
    settings.lastLanguages[0] ?? (isLang(settings.uiLang) ? settings.uiLang : 'fr');

  const quickPlay = async () => {
    setLoading(true);
    setError(false);
    try {
      await startLocalSession({ mode: 'classic', languages: [quickLang], bot: null });
      navigate('/play');
    } catch {
      setError(true);
      setLoading(false);
    }
  };

  return (
    <main className="page home">
      <header className="brand">
        <div className="logo" aria-hidden="true">
          <span>P</span>
          <span>A</span>
        </div>
        <h1>{APP_NAME}</h1>
        <p className="tagline">{t('app.tagline')}</p>
      </header>

      <section className="quick">
        {loading ? (
          <Spinner label={t('setup.loading')} />
        ) : (
          <button className="btn primary huge" onClick={() => void quickPlay()}>
            {t('home.quickPlay')}
            <span className="small">
              {t('home.quickPlayDesc', { lang: quickLang.toUpperCase() })}
            </span>
          </button>
        )}
        {error && (
          <p className="message error" role="alert">
            {t('errors.lexiconLoad')}
          </p>
        )}
      </section>

      <section className="modes-grid" aria-label={t('home.modesTitle')}>
        {(['classic', 'polyglot', 'duel', 'sprint', 'relax'] as const).map((m) => (
          <Link key={m} className="mode-card" href={`/setup/${m}`}>
            <strong>{t(`modes.${m}.name`)}</strong>
            <span className="muted small">{t(`modes.${m}.short`)}</span>
          </Link>
        ))}
      </section>

      <section className="online" aria-label={t('online.title')}>
        <h2>{t('online.title')}</h2>
        {me.loaded && !me.online && <p className="muted small">{t('online.offlineNote')}</p>}
        <div className="modes-grid">
          <Link className="mode-card" href="/challenge/new">
            <strong>{t('online.challengeFriend')}</strong>
            <span className="muted small">{t('online.challengeFriendDesc')}</span>
          </Link>
          <Link className="mode-card" href="/live">
            <strong>{t('online.liveDuel')}</strong>
            <span className="muted small">{t('online.liveDuelDesc')}</span>
          </Link>
        </div>
        <div className="modes-grid">
          <Link className="mode-card" href="/daily">
            <strong>{t('daily.title')}</strong>
            <span className="muted small">{t('daily.intro')}</span>
          </Link>
          <Link className="mode-card" href="/tournaments">
            <strong>{t('tournaments.title')}</strong>
            <span className="muted small">{t('tournaments.intro')}</span>
          </Link>
        </div>
        <nav className="home-links">
          <Link href="/me">{t('profile.title')}</Link>
          <Link href="/leaderboards">{t('boards.title')}</Link>
          <Link href="/account">
            {me.user?.kind === 'registered' ? me.user.displayName : t('nav.account')}
          </Link>
        </nav>
      </section>

      <label className="ui-lang">
        <span className="sr-only">{t('settings.uiLanguage')}</span>
        <select
          value={settings.uiLang}
          aria-label={t('settings.uiLanguage')}
          onChange={(e) => {
            const v = e.target.value as UiLang;
            updateSettings({ uiLang: v });
            void setUiLanguage(v);
          }}
        >
          {UI_LANGS.map((l) => (
            <option key={l} value={l}>
              {LANG_ENDONYMS[l]}
            </option>
          ))}
        </select>
      </label>

      <nav className="home-links">
        <Link href="/rules">{t('nav.rules')}</Link>
        <Link href="/settings">{t('nav.settings')}</Link>
        <Link href="/about">{t('nav.about')}</Link>
      </nav>
    </main>
  );
}
